// PlayerMove — a ordem exata do tick está em docs/02-MOVEMENT.md. NÃO reordenar.
import {
  GRAVITY, AIR_ACCELERATE, ACCELERATE, FRICTION, STOP_SPEED, MAX_VELOCITY,
  RUN_SPEED, AIR_CAP, JUMP_IMPULSE, STAND_ON_NORMAL, OVERBOUNCE,
  HULL_HALF_XZ, HULL_HEIGHT, HULL_HEIGHT_DUCK, DUCK_SPEED_MULT,
  GROUND_TRACE_DIST, MAX_GROUND_LAND_SPEED, TICK_DT,
} from '@surfrun/shared';
import { V3, v3, copy, set, dot, normalize, cross, scale, len } from './vec';
import { Brush, traceBox, clipVelocity, makeTrace } from './trace';

export interface InputCmd {
  fwd: number;    // -1..1
  side: number;   // -1..1 (direita positiva)
  jump: boolean;  // segurando (auto-hop)
  duck: boolean;
  yaw: number;    // graus, no momento do tick
  pitch: number;
}

export interface PlayerState {
  pos: V3;              // pé (centro da base da AABB)
  vel: V3;              // units/s
  ducked: boolean;
  grounded: boolean;
  groundNormal: V3;
  justJumped: boolean;  // eventos p/ áudio/efeitos (válidos por 1 tick)
  justLanded: boolean;
  landSpeed: number;    // |vel.y| no pouso
}

export function makePlayerState(): PlayerState {
  return {
    pos: v3(), vel: v3(), ducked: false, grounded: false,
    groundNormal: v3(0, 1, 0), justJumped: false, justLanded: false, landSpeed: 0,
  };
}

const DEG = Math.PI / 180;
const _wishdir = v3();
const _half = v3();
const _center = v3();
const _end = v3();
const _tr = makeTrace();

function hullHeight(s: PlayerState): number { return s.ducked ? HULL_HEIGHT_DUCK : HULL_HEIGHT; }

/** centro da AABB a partir do pé */
function centerOf(s: PlayerState, out: V3): V3 {
  return set(out, s.pos.x, s.pos.y + hullHeight(s) / 2, s.pos.z);
}
function halfOf(s: PlayerState, out: V3): V3 {
  return set(out, HULL_HALF_XZ, hullHeight(s) / 2, HULL_HALF_XZ);
}

/** wishdir no plano XZ a partir de yaw + input (docs/02 passo 1) */
function computeWish(cmd: InputCmd, out: V3): number {
  const yawRad = cmd.yaw * DEG;
  // forward no plano: (-sin(yaw), 0, -cos(yaw)) — convenção three.js (câmera olha -Z com yaw 0)
  const fx = -Math.sin(yawRad), fz = -Math.cos(yawRad);
  const rx = -fz, rz = fx; // right = forward × up
  out.x = fx * cmd.fwd + rx * cmd.side;
  out.y = 0;
  out.z = fz * cmd.fwd + rz * cmd.side;
  const l = Math.hypot(out.x, out.z);
  if (l > 1e-6) { out.x /= l; out.z /= l; }
  return l > 1e-6 ? 1 : 0;
}

function friction(s: PlayerState, dt: number): void {
  const speed = Math.hypot(s.vel.x, s.vel.z);
  if (speed < 0.1) { s.vel.x = 0; s.vel.z = 0; return; }
  const control = Math.max(speed, STOP_SPEED);
  const drop = control * FRICTION * dt;
  const newspeed = Math.max(speed - drop, 0) / speed;
  s.vel.x *= newspeed;
  s.vel.z *= newspeed;
}

function accelerate(s: PlayerState, wishdir: V3, wishspeed: number, accel: number, dt: number): void {
  const currentspeed = dot(s.vel, wishdir); // projeção — a essência do strafe
  const addspeed = wishspeed - currentspeed;
  if (addspeed <= 0) return;
  const accelspeed = Math.min(accel * wishspeed * dt, addspeed);
  s.vel.x += accelspeed * wishdir.x;
  s.vel.y += accelspeed * wishdir.y;
  s.vel.z += accelspeed * wishdir.z;
}

/** Categoriza chão: trace 2u pra baixo (docs/02 passo 2) */
function categorizeGround(s: PlayerState, brushes: readonly Brush[]): void {
  const wasGrounded = s.grounded;
  if (s.vel.y > MAX_GROUND_LAND_SPEED) {
    s.grounded = false;
    return;
  }
  centerOf(s, _center);
  halfOf(s, _half);
  set(_end, _center.x, _center.y - GROUND_TRACE_DIST, _center.z);
  const tr = traceBox(brushes, _center, _end, _half, _tr);
  if (tr.hit && tr.normal.y >= STAND_ON_NORMAL) {
    s.grounded = true;
    copy(s.groundNormal, tr.normal);
    // encosta no chão (remove o gap do trace)
    s.pos.y = tr.endPos.y - hullHeight(s) / 2;
    if (!wasGrounded) {
      s.justLanded = true;
      s.landSpeed = Math.abs(s.vel.y);
    }
  } else {
    s.grounded = false;
  }
}

const UNSTUCK_OFFSETS: [number, number, number][] = [
  [0, 0.25, 0], [0, 1, 0], [0.25, 0, 0], [-0.25, 0, 0], [0, 0, 0.25], [0, 0, -0.25], [0, 4, 0],
];
const _probe = v3();

/** Tenta desprender o player de sólido com pequenos deslocamentos. */
function tryUnstuck(s: PlayerState, brushes: readonly Brush[]): boolean {
  halfOf(s, _half);
  for (const [ox, oy, oz] of UNSTUCK_OFFSETS) {
    centerOf(s, _probe);
    _probe.x += ox; _probe.y += oy; _probe.z += oz;
    const tr = traceBox(brushes, _probe, _probe, _half, _tr);
    if (!tr.startSolid) {
      s.pos.x += ox; s.pos.y += oy; s.pos.z += oz;
      return true;
    }
  }
  return false;
}

const MAX_CLIP_PLANES = 5;
const _planes: V3[] = Array.from({ length: MAX_CLIP_PLANES }, () => v3());
const _primalVel = v3();
const _origVel = v3();
const _try = v3();
const _dir = v3();

/**
 * TryPlayerMove — colisão com deslize (PM_FlyMove do Quake / TryPlayerMove do Source).
 * A cada impacto, re-clipa a velocidade ORIGINAL contra TODOS os planos acumulados —
 * é isso que trata re-impactos na mesma face (surf!) sem zerar a velocidade.
 */
function tryPlayerMove(s: PlayerState, brushes: readonly Brush[], dt: number): void {
  copy(_primalVel, s.vel);
  copy(_origVel, s.vel);
  let timeLeft = dt;
  let numPlanes = 0;

  for (let bump = 0; bump < 4; bump++) {
    if (len(s.vel) < 1e-6) break;
    centerOf(s, _center);
    halfOf(s, _half);
    set(_end,
      _center.x + s.vel.x * timeLeft,
      _center.y + s.vel.y * timeLeft,
      _center.z + s.vel.z * timeLeft);
    let tr = traceBox(brushes, _center, _end, _half, _tr);

    if (tr.allSolid) {
      // preso em sólido (erro numérico perto de aresta): tenta nudges antes de desistir
      if (!tryUnstuck(s, brushes)) {
        set(s.vel, 0, 0, 0);
        return;
      }
      centerOf(s, _center);
      set(_end,
        _center.x + s.vel.x * timeLeft,
        _center.y + s.vel.y * timeLeft,
        _center.z + s.vel.z * timeLeft);
      tr = traceBox(brushes, _center, _end, _half, _tr);
      if (tr.allSolid) { set(s.vel, 0, 0, 0); return; }
    }
    if (tr.fraction > 0) {
      s.pos.x = tr.endPos.x;
      s.pos.y = tr.endPos.y - hullHeight(s) / 2;
      s.pos.z = tr.endPos.z;
      copy(_origVel, s.vel);
      numPlanes = 0;
    }
    if (tr.fraction === 1) break;

    timeLeft -= timeLeft * tr.fraction;

    // plano repetido (resting a DIST_EPSILON da face): nudge da velocidade pra fora
    // ao longo do normal e tenta de novo — PM_SlideMove do Quake 3. Sem isso o
    // player congela deslizando em rampas não-axiais.
    let dup = false;
    for (let k = 0; k < numPlanes; k++) {
      if (dot(tr.normal, _planes[k]!) > 0.99) {
        s.vel.x += tr.normal.x;
        s.vel.y += tr.normal.y;
        s.vel.z += tr.normal.z;
        dup = true;
        break;
      }
    }
    if (dup) continue;

    if (numPlanes >= MAX_CLIP_PLANES) { set(s.vel, 0, 0, 0); break; }
    copy(_planes[numPlanes]!, tr.normal);
    numPlanes++;

    // re-clipa a velocidade original até ficar paralela a todos os planos
    let i = 0;
    for (; i < numPlanes; i++) {
      copy(_try, _origVel);
      clipVelocity(_try, _planes[i]!, OVERBOUNCE);
      let ok = true;
      for (let j = 0; j < numPlanes; j++) {
        if (j !== i && dot(_try, _planes[j]!) < 0) { ok = false; break; }
      }
      if (ok) break;
    }

    if (i < numPlanes) {
      copy(s.vel, _try);
    } else {
      // nenhum clip individual serve — desliza pela aresta entre 2 planos
      if (numPlanes !== 2) { set(s.vel, 0, 0, 0); break; }
      cross(_dir, _planes[0]!, _planes[1]!);
      normalize(_dir, _dir);
      const d = dot(_dir, s.vel);
      scale(s.vel, _dir, d);
    }

    // se está indo contra a direção original, para (evita vibrar em cantos)
    if (dot(s.vel, _primalVel) <= 0) { set(s.vel, 0, 0, 0); break; }
  }
}

/** Um tick completo de movimento. */
export function playerMove(s: PlayerState, cmd: InputCmd, brushes: readonly Brush[], dt: number = TICK_DT): void {
  s.justJumped = false;
  s.justLanded = false;

  // 1. wishdir
  const hasWish = computeWish(cmd, _wishdir);
  s.ducked = cmd.duck;

  // 2. categorizar
  categorizeGround(s, brushes);

  if (s.grounded) {
    if (cmd.jump) {
      // 3a. auto-bhop: pula JÁ, sem fricção neste tick — preserva velocidade
      s.vel.y = JUMP_IMPULSE;
      s.grounded = false;
      s.justJumped = true;
      // meio tick de gravidade + movimento aéreo
      s.vel.y -= GRAVITY * dt * 0.5;
      if (hasWish) accelerate(s, _wishdir, Math.min(RUN_SPEED, AIR_CAP), AIR_ACCELERATE, dt);
      tryPlayerMove(s, brushes, dt);
      s.vel.y -= GRAVITY * dt * 0.5;
    } else {
      // 3b. chão: fricção + accelerate
      s.vel.y = 0;
      friction(s, dt);
      if (hasWish) {
        const wishspeed = RUN_SPEED * (s.ducked ? DUCK_SPEED_MULT : 1);
        accelerate(s, _wishdir, wishspeed, ACCELERATE, dt);
      }
      s.vel.y = 0;
      tryPlayerMove(s, brushes, dt);
    }
  } else {
    // 4. ar: airaccelerate + gravidade em meio-tick
    s.vel.y -= GRAVITY * dt * 0.5;
    if (hasWish) accelerate(s, _wishdir, Math.min(RUN_SPEED, AIR_CAP), AIR_ACCELERATE, dt);
    tryPlayerMove(s, brushes, dt);
    s.vel.y -= GRAVITY * dt * 0.5;
  }

  // 6. clamp por eixo
  s.vel.x = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, s.vel.x));
  s.vel.y = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, s.vel.y));
  s.vel.z = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, s.vel.z));
}
